import { AUTH_REDIRECT_KEY, LOGIN_PATH } from '@/auth/authSession'
import {
  Avatar,
  AvatarFallback,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui'
import { useProjectTeamMembers } from '@/hooks'
import { useAuth, useCurrentUser } from '@/hooks/useAuth'
import { RoleNames } from '@/lib/permissions'
import { queryClient } from '@/lib/queryClient'
import { getDisplayRoles, getInitials } from '@/lib/utils'
import { clearSelectedTenantId } from '@/lib/tenantStorage'
import { useMsal } from '@azure/msal-react'
import { LogOut, Settings } from 'lucide-react'
import { useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

export function UserProfileMenu({ showSettings = true, collapsed = false }: { showSettings?: boolean; collapsed?: boolean } = {}) {
  const { data: user } = useCurrentUser()
  const { isAdmin } = useAuth()
  const { instance } = useMsal()
  const navigate = useNavigate()
  const { projectId } = useParams()
  const { data: teamMembers } = useProjectTeamMembers(projectId)

  const msalName = instance.getActiveAccount()?.name ?? ''
  const firstName = user?.firstName ?? ''
  const lastName = user?.lastName ?? ''
  const displayName = firstName && lastName ? `${firstName} ${lastName}` : user?.displayName ?? msalName
  const initials = firstName && lastName
    ? `${firstName[0]}${lastName[0]}`.toUpperCase()
    : displayName ? getInitials(displayName) : ''

  const roles = useMemo(() => {
    if (!projectId) {
      return null
    }

    if (isAdmin) {
      return RoleNames.Admin
    }

    if (!user || !teamMembers) {
      return null
    }

    const currentUserMember = teamMembers.find(m => m.userId === user.id)
    if (!currentUserMember || currentUserMember.roles.length === 0) {
      return null
    }

    return getDisplayRoles(currentUserMember.roles.map(r => r.name))
  }, [projectId, isAdmin, user, teamMembers])

  const handleSignOut = () => {
    queryClient.clear()
    clearSelectedTenantId()
    sessionStorage.removeItem(AUTH_REDIRECT_KEY)
    instance.logoutRedirect({ postLogoutRedirectUri: LOGIN_PATH })
  }

  return (
    <div className={`border-t ${collapsed ? 'p-2' : 'p-4'}`}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            aria-label="User menu"
            data-testid="user-profile-menu"
            className={`flex w-full items-center rounded-md py-2 text-sm transition-colors hover:bg-muted ${collapsed ? 'justify-center px-0' : 'gap-3 px-2'
              }`}
          >
            <Avatar className="h-9 w-9 shrink-0">
              <AvatarFallback className="bg-primary text-primary-foreground text-xs font-semibold">
                {initials}
              </AvatarFallback>
            </Avatar>
            {!collapsed && (
              <div className="flex-1 text-left min-w-0">
                <p className="truncate font-medium text-foreground">{displayName}</p>
                {roles && (
                  <p className="text-xs text-muted-foreground">{roles}</p>
                )}
              </div>
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="top" align="start" className="w-56">
          {showSettings && (
            <DropdownMenuItem onClick={() => navigate('/settings')}>
              <Settings className="mr-2 h-4 w-4" />
              Settings
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={handleSignOut}>
            <LogOut className="mr-2 h-4 w-4" />
            Sign Out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
